import { type UseQueryOptions, useQuery } from '@tanstack/react-query';
import {
  type GetImageOrderV1PortfoliosPortfolioIdPdpsPdpIdImageOrderGetData,
  type GetImageOrderV1PortfoliosPortfolioIdPdpsPdpIdImageOrderGetError,
  type GetImageOrderV1PortfoliosPortfolioIdPdpsPdpIdImageOrderGetResponse,
  getImageOrderV1PortfoliosPortfolioIdPdpsPdpIdImageOrderGet,
} from '../../generated';

export type GetImageOrderQueryVariables = GetImageOrderV1PortfoliosPortfolioIdPdpsPdpIdImageOrderGetData['path'];

export type GetImageOrderQuery = GetImageOrderV1PortfoliosPortfolioIdPdpsPdpIdImageOrderGetResponse;

export const useGetImageOrderQuery = <
  TData = GetImageOrderQuery,
  TError = GetImageOrderV1PortfoliosPortfolioIdPdpsPdpIdImageOrderGetError,
>(
  { portfolio_id, pdp_id }: GetImageOrderQueryVariables,
  options?: Omit<UseQueryOptions<GetImageOrderQuery, TError, TData>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery<GetImageOrderQuery, TError, TData>({
    queryKey: ['GetImageOrder', { portfolio_id, pdp_id }],
    queryFn: async () => {
      const { data } = await getImageOrderV1PortfoliosPortfolioIdPdpsPdpIdImageOrderGet({
        path: { portfolio_id, pdp_id },
        throwOnError: true,
      });
      return data as GetImageOrderQuery;
    },
    ...options,
  });
};

useGetImageOrderQuery.getKey = ({ portfolio_id, pdp_id }: GetImageOrderQueryVariables) => [
  'GetImageOrder',
  { portfolio_id, pdp_id },
];

useGetImageOrderQuery.fetcher = (
  { portfolio_id, pdp_id }: GetImageOrderQueryVariables,
  _headers?: RequestInit['headers'],
) => {
  return async () => {
    const { data } = await getImageOrderV1PortfoliosPortfolioIdPdpsPdpIdImageOrderGet({
      path: { portfolio_id, pdp_id },
      throwOnError: true,
    });
    return data as GetImageOrderQuery;
  };
};

useGetImageOrderQuery.schema = {
  method: 'get',
  path: '/v1/portfolios/{portfolio_id}/pdps/{pdp_id}/image-order',
};
